import type { ResponsiveImageSource } from '@common/types/file.type';
import type { SelectedFileLegacy } from '@sites/entity-core';

const DEFAULT_IMAGE_THUMBNAIL_PREVIEW = './assets/img/elements/gallery-blank.svg';
const RESIZE_SEGMENT = 'resize_landing';
const THUMB_SEGMENT = 'thumb';

/**
 * Pages that must be served the CDN originals instead of the `resize_landing`
 * webp variants. Hardcoded stopgap — there is no per-page resize setting yet,
 * and `hasResize` lives inside each stored file object.
 */
const RESIZE_DISABLED_PAGE_IDS: ReadonlySet<number> = new Set([704745, 704746, 704747]);

export const isResizeDisabledForPage = (pageId?: number | string | null): boolean =>
    pageId !== null && pageId !== undefined && RESIZE_DISABLED_PAGE_IDS.has(Number(pageId));

export const LANDING_SRCSET_BREAKPOINTS: Omit<ResponsiveImageSource, 'url'>[] = [
    {
        size: 720,
        media: '(max-width: 720px)',
    },
    {
        size: 1080,
        media: '(max-width: 1080px)',
    },
    {
        size: 1920,
        media: '(min-width: 1081px)',
    },
];
export type SrcSetBreakpoints = (typeof LANDING_SRCSET_BREAKPOINTS)[number]['size'];

export const formatFileUrl = (file: SelectedFileLegacy, baseUrl: string): string => {
    if (!file) return '';

    if (file?.url?.startsWith('http')) {
        return file.url;
    }

    let url = DEFAULT_IMAGE_THUMBNAIL_PREVIEW;

    if (file?.url && file.defaultCdnUrl) {
        return encodeURI(baseUrl + file.url);
    }

    if (file?.url) {
        url = encodeURI(file.url);
    }

    return `${baseUrl}${url}`;
};

export const resolveCdnBaseUrl = (defaultCdnUrl: string | undefined, fallbackBaseUrl: string): string => {
    if (!defaultCdnUrl) return fallbackBaseUrl;
    return defaultCdnUrl.startsWith('http') ? defaultCdnUrl : `https://${defaultCdnUrl}`;
};

export const encodeFileUrl = (file: SelectedFileLegacy, baseUrl: string): string => {
    return `url('${formatFileUrl(file, baseUrl)}')`;
};

export const fileNameWithPath = (path: string): string => path.split('/').pop();

const formatResizeBaseUrl = (file: SelectedFileLegacy, baseUrl: string): string => {
    if (!file?.url) return '';

    const base = new URL(baseUrl);
    const url = new URL(file.url, base.origin);

    const baseSegments = base.pathname.split('/').filter(Boolean);
    const segments = url.pathname.split('/').filter(Boolean);

    if (segments.length === 0) {
        url.pathname = '/' + baseSegments.join('/');
        return url.toString();
    }

    const [userHash, ...rest] = segments;

    if (rest.length > 0) {
        // replace the last dot in the filename with an underscore
        // regex matches the last dot not followed by another dot and captures the extension
        rest[rest.length - 1] = rest.at(-1).replace(/\.([^.]+)$/, '_$1');
    }

    url.pathname = '/' + [...baseSegments, userHash, RESIZE_SEGMENT, ...rest].join('/');
    return url.toString();
};

const supportsResize = (file: SelectedFileLegacy): boolean => {
    if (!file) return false;

    const { hasResize, url, extension } = file;
    const isSupportedFormat = ['gif', 'svg'].every((format) => !extension?.includes(format));
    return hasResize && url && !url.startsWith('http') && isSupportedFormat;
};

const supportsThumbPreview = (file: SelectedFileLegacy): boolean => {
    if (!file) return false;

    const { url, extension } = file;
    const isSupportedFormat = ['gif', 'svg'].every((format) => !extension?.includes(format));
    return url && !url.startsWith('http') && isSupportedFormat;
};

export const formatFileThumbUrl = (file: SelectedFileLegacy, baseUrl: string): string => {
    if (!supportsThumbPreview(file)) return '';

    const base = new URL(baseUrl);
    const url = new URL(file.url, base.origin);

    const baseUrlSegments = base.pathname.split('/').filter(Boolean);
    const [userHash, ...rest] = url.pathname.split('/').filter(Boolean);
    const fileName = rest.at(-1);

    if (!userHash || !fileName) return '';

    const segments = [...baseUrlSegments, userHash, THUMB_SEGMENT, ...rest];
    url.pathname = '/' + segments.filter(Boolean).join('/');
    return url.toString();
};

const resolveResponsiveImageUrl = (
    size: number | undefined,
    fallbackUrl: string,
    resizeBaseUrl: string | null,
    file: SelectedFileLegacy
): string => {
    if (!resizeBaseUrl) {
        return fallbackUrl;
    }

    if (size === undefined) {
        return `${resizeBaseUrl}/source.webp`;
    }

    if (size >= Number(file.imageWidth)) {
        return file.hasSourceResize ? `${resizeBaseUrl}/source.webp` : fallbackUrl;
    }

    return `${resizeBaseUrl}/${size}.webp`;
};

export const buildFileSrcSets = (file: SelectedFileLegacy, baseUrl: string): ResponsiveImageSource[] => {
    const fallbackUrl = formatFileUrl(file, baseUrl);
    const resizeBaseUrl = supportsResize(file) ? formatResizeBaseUrl(file, baseUrl) : null;

    return LANDING_SRCSET_BREAKPOINTS.map(({ size, media }) => ({
        size,
        media,
        url: resolveResponsiveImageUrl(size, fallbackUrl, resizeBaseUrl, file),
    }));
};

export const formatFileUrlWithResize = (
    file: SelectedFileLegacy,
    baseUrl: string,
    size?: SrcSetBreakpoints
): string => {
    const fallbackUrl = formatFileUrl(file, baseUrl);
    const resizeBaseUrl = supportsResize(file) ? formatResizeBaseUrl(file, baseUrl) : null;

    return resolveResponsiveImageUrl(size, fallbackUrl, resizeBaseUrl, file);
};

export const buildFilePreviewUrls = (file: SelectedFileLegacy, baseUrl: string): string[] => {
    const fallbackUrl = formatFileUrl(file, baseUrl);
    const urls = [formatFileThumbUrl(file, baseUrl), formatFileUrlWithResize(file, baseUrl, 720), fallbackUrl];

    return urls.filter(Boolean);
};

export const encodeFileUrlWithResize = (
    file: SelectedFileLegacy,
    baseUrl: string,
    size?: SrcSetBreakpoints
): string => {
    const fallbackUrl = formatFileUrl(file, baseUrl);
    const resizedUrl = formatFileUrlWithResize(file, baseUrl, size);

    if (resizedUrl === fallbackUrl) {
        return `url('${fallbackUrl}')`;
    }

    return `url('${resizedUrl}'), url('${fallbackUrl}')`;
};
